import { api } from './api.ts';
import { player } from './player.ts';

/**
 * Telling Plex where somebody got to.
 *
 * THIS IS THE OBLIGATION THAT CAME WITH CHOOSING PLEX. Resume points are only
 * correct if clients report them, and every other client on that server does.
 * If ours does not, seventeen people slowly stop having a working Continue
 * Watching — a failure that is silent, gradual, and only visible to other
 * people, which is the worst combination a bug can have.
 *
 * Three rules, each learned from how the equivalent goes wrong elsewhere:
 *
 *  1. REPORT ON A TIMER, not only on events. A player that runs for an hour
 *     and is then killed by the system emits no stop event at all, and an
 *     hour of watching is lost. Ten seconds is frequent enough that nothing
 *     meaningful is ever lost and rare enough to be invisible.
 *
 *  2. REPORT THE STOP LAST AND UNCONDITIONALLY, including as the page goes
 *     away. It is the most valuable report of the set and the easiest to
 *     lose — hence `keepalive` on the request.
 *
 *  3. NEVER LET IT BREAK PLAYBACK. A failed report costs a few seconds of
 *     resume accuracy. Anything that turns it into a visible error has
 *     traded something nobody notices for something everybody does.
 */

const TICK_MS = 10_000;

let timer: ReturnType<typeof setInterval> | null = null;
let itemId: string | null = null;
let lastState: 'playing' | 'paused' | 'stopped' | null = null;

/** Where the <video> element actually is, in milliseconds. */
function position(): { timeMs: number; durationMs: number } {
  const el = document.querySelector('video');
  const timeMs = Math.round((el?.currentTime ?? 0) * 1000);
  const durationMs = Number.isFinite(el?.duration) ? Math.round((el!.duration) * 1000) : 0;
  return { timeMs, durationMs };
}

function send(state: 'playing' | 'paused' | 'stopped'): void {
  if (!itemId) return;
  const { timeMs, durationMs } = position();
  // A report of zero at the very start would overwrite a real resume point
  // with the beginning of the film, which is worse than not reporting.
  if (state !== 'stopped' && timeMs === 0) return;
  void api.libraryProgress(itemId, { state, timeMs, durationMs }).catch(() => {
    /* Rule 3. A dropped report is not worth a word to anybody. */
  });
}

function stopTimer(): void {
  if (timer) { clearInterval(timer); timer = null; }
}

/**
 * Follow the player and report as it goes.
 *
 * Bound once at startup, like the now-playing subscription — a component
 * owning this would stop reporting the moment somebody navigated away from
 * the screen they started playback on, which is most of the time.
 */
export function bindProgressReporting(): () => void {
  const unsubscribe = player.subscribe((state) => {
    // The id the SERVER resolved, not the poster that was pressed: a series
    // has no position of its own, and reporting against it would lose the
    // episode's place.
    const id = state.playbackId ?? null;

    // Something else started, or playback ended. Report the stop for whatever
    // was playing BEFORE picking up the new one.
    if (itemId && id !== itemId) {
      send('stopped');
      stopTimer();
      itemId = null;
      lastState = null;
    }

    if (!id) return;
    itemId = id;

    if (state.status === 'playing') {
      if (lastState !== 'playing') send('playing');
      lastState = 'playing';
      if (!timer) timer = setInterval(() => send('playing'), TICK_MS);
    } else if (state.status === 'paused') {
      if (lastState !== 'paused') send('paused');
      lastState = 'paused';
      stopTimer();
    }
  });

  // The page going away is the one moment a stop report matters most and is
  // least likely to be sent. `visibilitychange` rather than `unload`, which
  // mobile browsers do not reliably fire at all.
  const onHide = () => { if (document.visibilityState === 'hidden') send('stopped'); };
  document.addEventListener('visibilitychange', onHide);

  return () => {
    unsubscribe();
    stopTimer();
    document.removeEventListener('visibilitychange', onHide);
  };
}
